// US-WITHDRAWAL-02 -- child requests a withdrawal from an approval-required bucket.

import {
  type AppError,
  type BucketEntry,
  type WithdrawalSubmitPendingResult,
  withdrawalSubmitPending,
} from '@chorepop/core/client';
import { SUPPORTED_MONEY_CURRENCIES } from '@chorepop/core/domain';
import { type Component, For, Show, createSignal } from 'solid-js';
import { type CloudFxRate, fetchDailyFxRate } from '../cloud/cloudAuth';
import { ActionScreen } from '../components/ActionScreen';
import { AmountPad } from '../components/AmountPad';

type Props = {
  sessionId: string;
  bucket: BucketEntry;
  currencyCode: string;
  onSubmitted: (result: WithdrawalSubmitPendingResult) => void;
  onCancel: () => void;
};

const REASONS = [
  { code: 'toy', label: 'Toy' },
  { code: 'food_snack', label: 'Food or snack' },
  { code: 'video_game', label: 'Video game' },
  { code: 'book_media', label: 'Book or media' },
  { code: 'clothing', label: 'Clothing' },
  { code: 'gift', label: 'Gift for someone' },
  { code: 'experience', label: 'Outing or experience' },
  { code: 'charitable_giving', label: 'Charitable giving' },
  { code: 'other', label: 'Other' },
];

export const SubmitPendingWithdrawalScreen: Component<Props> = (props) => {
  const [amountInput, setAmountInput] = createSignal('');
  const [reasonCode, setReasonCode] = createSignal('toy');
  const [note, setNote] = createSignal('');
  const [sourceCurrency, setSourceCurrency] = createSignal(props.currencyCode);
  const [fxRate, setFxRate] = createSignal<CloudFxRate | null>(null);
  const [loadingRate, setLoadingRate] = createSignal(false);
  const [submitting, setSubmitting] = createSignal(false);
  const [errorMessage, setErrorMessage] = createSignal<string | null>(null);

  const parseMinorUnits = (): number | null => {
    const raw = amountInput().trim();
    if (raw === '' || !/^\d+(\.\d{1,2})?$/.test(raw)) return null;
    const major = Number.parseFloat(raw);
    return Number.isFinite(major) && major > 0 ? Math.round(major * 100) : null;
  };

  const isForeignSpend = () => sourceCurrency() !== props.currencyCode;
  const ledgerMinorUnits = (): number | null => {
    const sourceMinor = parseMinorUnits();
    if (sourceMinor === null) return null;
    if (!isForeignSpend()) return sourceMinor;
    const rate = fxRate();
    if (!rate) return null;
    const converted = Math.round((sourceMinor * rate.ratePpm) / 1_000_000);
    return Number.isSafeInteger(converted) && converted > 0 ? converted : null;
  };
  const exceedsAvailableBalance = () => {
    const minorUnits = ledgerMinorUnits();
    return minorUnits !== null && minorUnits > props.bucket.balanceMinorUnits;
  };

  const formatBalance = (minor: number) => {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: props.currencyCode,
      }).format(minor / 100);
    } catch {
      return `${props.currencyCode} ${(minor / 100).toFixed(2)}`;
    }
  };

  const handleSubmit = async () => {
    setErrorMessage(null);
    const sourceMinorUnits = parseMinorUnits();
    const minorUnits = ledgerMinorUnits();
    if (sourceMinorUnits === null) {
      setErrorMessage('Enter an amount greater than zero (for example, 1 or 2.50).');
      return;
    }
    if (minorUnits === null) {
      setErrorMessage(
        `Wait for the latest daily exchange rate, or switch back to ${props.currencyCode}.`,
      );
      return;
    }
    if (minorUnits > props.bucket.balanceMinorUnits) {
      setErrorMessage(
        `Not enough in ${props.bucket.bucketName} — you have ${formatBalance(props.bucket.balanceMinorUnits)}.`,
      );
      return;
    }

    setSubmitting(true);
    try {
      const trimmedNote = note().trim();
      const currentFx = isForeignSpend() ? fxRate() : null;
      const result = await withdrawalSubmitPending({
        sessionId: props.sessionId,
        bucketId: props.bucket.bucketId,
        amountMinorUnits: minorUnits,
        reasonCode: reasonCode(),
        ...(trimmedNote === '' ? {} : { reasonNote: trimmedNote }),
        ...(currentFx
          ? {
              fx: {
                sourceCurrencyCode: sourceCurrency(),
                sourceAmountMinorUnits: sourceMinorUnits,
                sourceToGbpRatePpm: currentFx.ratePpm,
                effectiveDate: currentFx.effectiveDate,
                provider: currentFx.provider,
              },
            }
          : {}),
      });
      if (!result.ok) {
        setErrorMessage(humanReadableError(result.error));
        return;
      }
      props.onSubmitted(result.value);
    } finally {
      setSubmitting(false);
    }
  };

  const changeCurrency = async (currency: string) => {
    setSourceCurrency(currency);
    setFxRate(null);
    setErrorMessage(null);
    if (currency === props.currencyCode) return;
    setLoadingRate(true);
    try {
      setFxRate(await fetchDailyFxRate(currency, props.currencyCode === 'USD' ? 'USD' : 'GBP'));
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'Could not load the daily exchange rate.',
      );
    } finally {
      setLoadingRate(false);
    }
  };

  return (
    <ActionScreen
      eyebrow="Goes to Mum & Dad first"
      title="Spend"
      onBack={props.onCancel}
      class="action-screen--child-money"
      footer={
        <button
          type="button"
          class="primary action-screen-primary"
          disabled={submitting() || exceedsAvailableBalance()}
          onClick={() => void handleSubmit()}
        >
          {submitting() ? 'Sending…' : 'Ask Mum & Dad'}
        </button>
      }
    >
      <AmountPad
        id="pending-withdrawal-amount"
        label="Amount"
        value={amountInput()}
        currencyCode={sourceCurrency()}
        hint={
          exceedsAvailableBalance()
            ? `That is more than ${props.bucket.bucketName} has. Try a smaller amount.`
            : `Leaves ${formatBalance(Math.max(0, props.bucket.balanceMinorUnits - (ledgerMinorUnits() ?? 0)))} in ${props.bucket.bucketName} once approved.`
        }
        disabled={submitting()}
        onChange={(value) => {
          setAmountInput(value);
          setErrorMessage(null);
        }}
      />

      <label class="action-note-field" for="pending-withdrawal-currency">
        <span>Currency</span>
        <select
          id="pending-withdrawal-currency"
          disabled={submitting()}
          value={sourceCurrency()}
          onChange={(event) => void changeCurrency(event.currentTarget.value)}
        >
          <For each={SUPPORTED_MONEY_CURRENCIES}>
            {(currency) => <option value={currency.code}>{currency.label}</option>}
          </For>
        </select>
      </label>
      <Show when={isForeignSpend()}>
        <p class="action-helper-text" aria-live="polite">
          <Show when={loadingRate()}>Loading the daily rate…</Show>
          <Show when={!loadingRate() && fxRate()}>
            {(rate) => (
              <>
                {formatBalance(ledgerMinorUnits() ?? 0)} once approved · {rate().effectiveDate}{' '}
                daily rate. Source: ECB statistics via Frankfurter; ChorePop calculates the
                family-currency amount.
              </>
            )}
          </Show>
          <Show when={!loadingRate() && !fxRate()}>
            A daily rate is needed to record this request.
          </Show>
        </p>
      </Show>

      <section class="action-choice-section" aria-labelledby="pending-withdrawal-reason-label">
        <span id="pending-withdrawal-reason-label" class="action-field-label">
          What is it for?
        </span>
        <div class="action-chip-row">
          <For each={REASONS}>
            {(reason) => (
              <button
                type="button"
                class={`action-chip${reasonCode() === reason.code ? ' is-selected' : ''}`}
                aria-pressed={reasonCode() === reason.code}
                disabled={submitting()}
                onClick={() => setReasonCode(reason.code)}
              >
                {reason.label}
              </button>
            )}
          </For>
        </div>
      </section>

      <label class="action-note-field" for="pending-withdrawal-note">
        <span>
          Tell Mum or Dad more <em>Optional</em>
        </span>
        <input
          id="pending-withdrawal-note"
          type="text"
          autocomplete="off"
          disabled={submitting()}
          placeholder="For example, a new book"
          value={note()}
          onInput={(event) => setNote(event.currentTarget.value)}
        />
      </label>
      <Show when={errorMessage()}>
        {(message) => (
          <div class="form-error" role="alert">
            {message()}
          </div>
        )}
      </Show>
    </ActionScreen>
  );
};

function humanReadableError(error: AppError): string {
  switch (error.kind) {
    case 'validation':
    case 'network_required':
      return error.message;
    case 'unauthorized':
      return 'Your session has expired. Please sign in again.';
    case 'not_found':
      return 'That bucket is no longer available.';
    default:
      return `Something went wrong: ${error.message}`;
  }
}
